/*
 * Dusk Oak, quiet motion and points of sale.
 * Everything here is transform and opacity only, and the whole
 * file no-ops for a visitor who has asked to reduce motion, apart
 * from the recently viewed store, which is data not movement.
 * Loaded with defer, so the DOM is ready when it runs.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------
   * Section reveals (5.1b)
   * The html.dusk-motion gate is set inline in the head, so the
   * hidden state only ever exists when motion is allowed and JS
   * is present. Reveal once, never re-hide on scroll up.
   * ------------------------------------------------------ */
  function setupReveals() {
    if (reduce || !('IntersectionObserver' in window)) return;
    var targets = document.querySelectorAll('.dusk-band, .dusk-reveal, .dusk-reveal-group');
    if (!targets.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------------------------------
   * Add to cart, quiet confirm on the sticky bar CTA (5.1e)
   * The main button keeps the theme's own feedback. This only
   * touches our own CTA, so there is no race with theme JS.
   * ------------------------------------------------------ */
  function flashAdded(btn) {
    if (!btn || btn.classList.contains('dusk-added')) return;
    btn.classList.add('dusk-added');
    setTimeout(function () { btn.classList.remove('dusk-added'); }, 2000);
  }

  /* --------------------------------------------------------
   * Sticky buy bar (5.2a)
   * Docks when the real buy box leaves the viewport. The CTA
   * defers to the theme's add to cart button so variant and
   * quantity handling is reused rather than reimplemented.
   * ------------------------------------------------------ */
  function setupStickyBar() {
    var bar = document.querySelector('.dusk-buybar');
    if (!bar) return;
    var anchorSel = bar.getAttribute('data-anchor');
    var anchor = anchorSel && document.querySelector(anchorSel);
    var realBtn = document.querySelector('add-to-cart-component button[name="add"], .add-to-cart-button');
    var cta = bar.querySelector('.dusk-buybar__cta');
    var priceEl = bar.querySelector('.dusk-buybar__price');

    // Keep the bar price in step with the product page price.
    var mainPrice = document.querySelector('.product-details product-price, product-price');
    function syncPrice() {
      if (mainPrice && priceEl) {
        var t = (mainPrice.textContent || '').trim().replace(/\s+/g, ' ');
        if (t) priceEl.textContent = t;
      }
    }
    syncPrice();

    if (cta && realBtn) {
      cta.addEventListener('click', function () {
        if (realBtn.disabled) return;
        realBtn.click();
        flashAdded(cta);
      });
      // Mirror the disabled (sold out) state.
      cta.disabled = realBtn.disabled;
    }
    if (anchor && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          // Show the bar once the real buy box has scrolled out of view upward.
          var below = entry.boundingClientRect.top < 0;
          if (!entry.isIntersecting && below) {
            bar.classList.add('is-visible');
          } else {
            bar.classList.remove('is-visible');
          }
          syncPrice();
        });
      }, { threshold: 0 });
      io.observe(anchor);
    }

    // Periodically resync the price after variant changes.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('.product-details')) {
        setTimeout(syncPrice, 300);
        if (cta && realBtn) cta.disabled = realBtn.disabled;
      }
    });
  }

  /* --------------------------------------------------------
   * Recently viewed (5.2e)
   * A localStorage strip, four cards maximum, never the current
   * product, only rendered when at least two others exist.
   * ------------------------------------------------------ */
  var STORE_KEY = 'duskoak:recently-viewed';

  function readStore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; }
    catch (e) { return []; }
  }
  function writeStore(list) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (e) {}
  }
  function setupRecentlyViewed() {
    var host = document.querySelector('[data-dusk-recent]');
    var current = document.querySelector('[data-dusk-current-product]');

    // Record the current product first, if we are on a product page.
    if (current) {
      var item = {
        handle: current.getAttribute('data-handle'),
        title: current.getAttribute('data-title'),
        url: current.getAttribute('data-url'),
        image: current.getAttribute('data-image'),
        price: current.getAttribute('data-price')
      };
      if (item.handle) {
        var list = readStore().filter(function (p) { return p.handle !== item.handle; });
        list.unshift(item);
        writeStore(list.slice(0, 8));
      }
    }

    if (!host) return;
    var currentHandle = host.getAttribute('data-current-handle');
    var stored = readStore().filter(function (p) { return p.handle && p.handle !== currentHandle; });
    if (stored.length < 2) return;

    var cards = stored.slice(0, 4).map(function (p) {
      var img = p.image
        ? '<img class="dusk-recent__img" src="' + p.image + '" alt="" loading="lazy" width="600" height="600">'
        : '';
      var price = p.price ? '<span class="dusk-recent__price">' + p.price + '</span>' : '';
      return '<li class="dusk-recent__card"><a class="dusk-recent__link" href="' + p.url + '">' +
        '<span class="dusk-recent__media">' + img + '</span>' +
        '<span class="dusk-recent__name">' + p.title + '</span>' + price +
        '</a></li>';
    }).join('');

    host.querySelector('[data-dusk-recent-list]').innerHTML = cards;
    host.hidden = false;
    // Reveal it like the rest once it has content.
    if (!reduce) host.classList.add('dusk-reveal');
  }

  /* --------------------------------------------------------
   * Living gallery, reduced-motion guard (5.1d)
   * Gallery videos are set to autoplay for ambient motion. For a
   * visitor who asked to reduce motion, pause them and let the
   * poster and the theme's own play control take over.
   * ------------------------------------------------------ */
  function guardGalleryVideos() {
    if (!reduce) return;
    var vids = document.querySelectorAll('.product-media-container--video video, deferred-media[autoplay] video');
    vids.forEach(function (v) {
      try {
        v.autoplay = false;
        v.removeAttribute('autoplay');
        v.pause();
        v.setAttribute('controls', 'controls');
      } catch (e) {}
    });
  }

  function init() {
    setupReveals();
    guardGalleryVideos();
    setupStickyBar();
    setupRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
